import { FC, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MuiLink from '@mui/material/Link'
import IconButton from '@mui/material/IconButton'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { Link, useLocation } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import footer from '@config/footer/section-links.json'
import { Grid } from '@mui/material'
import useHeaderDetails from '@header/useHeaderDetails'
import useAdobeAnalytics, {
  PATIENT_EVENTS,
  CLINICIAN_EVENTS,
  SHARED_EVENTS,
} from '@analytics/adobe'
import { LinkType } from '@analytics/adobe/typing'
import { PROVIDERS } from '../../route.const'

export interface ILink {
  name: string
  href: string
  type?: string
}

export interface IFooter {
  footNote: {
    label: string
    primaryLinks: ILink[]
    secondaryLinks: ILink[]
  }
}

const footerData: IFooter = footer.data
const { footNote } = footerData

const FooterLink: FC<{
  link: ILink
  onClick: () => void
}> = ({ link, onClick }) => {
  const { login, role } = useHeaderDetails()

  return (
    <MuiLink
      noWrap
      component={Link}
      variant={'body2'}
      to={
        link.type === 'static'
          ? window.location.origin + link.href
          : link.name === 'Home' && login
            ? `/${role}`
            : link.href
      }
      target={
        link.href.startsWith('http') || link.type === 'static'
          ? '_blank'
          : undefined
      }
      // onClick={link.type === 'static' ? undefined : onClick}
      onClick={onClick}
    >
      {link.name}
    </MuiLink>
  )
}

const Footer: FC = () => {
  const { login, role } = useHeaderDetails()
  const { trackPage, trackLink } = useAdobeAnalytics()
  const location = useLocation()
  const [appVersion, setAppVersion] = useState<string>('')
  const { enableOptumMatchVersion } = useFlags()

  useEffect(() => {
    // Fetch version from version.json (injected during deployment)
    fetch('/version.json')
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
        throw new Error('Version file not found')
      })
      .then((data) => {
        if (data.version) {
          setAppVersion(data.version)
        }
      })
      .catch(() => {
        // Silently handle error if version file not found
      })
  }, [])

  const handleLinkClick = (
    linkName: string,
    linkType: LinkType,
    url?: string
  ) => {
    // Check if clinician is logged in
    const isClinician = login && role === PROVIDERS

    // Map link names to page tracking tags and link tracking tags
    let pageTag = ''
    let linkTag = ''

    switch (linkName.trim().toLowerCase()) {
      case 'home':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.HOME
            : PATIENT_EVENTS.PAGE.HOMEPAGE
          : SHARED_EVENTS.PAGE.HOMEPAGE
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_HOME
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_HOME
          : SHARED_EVENTS.LINK_CLICK.FOOTER_HOME
        break
      case 'about us':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.ABOUT_US
            : PATIENT_EVENTS.PAGE.ABOUT_US
          : SHARED_EVENTS.PAGE.ABOUT_US
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_ABOUT_US
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_ABOUT_US
          : SHARED_EVENTS.LINK_CLICK.FOOTER_ABOUT_US
        break
      case 'contact us':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.CONTACT_US
            : PATIENT_EVENTS.PAGE.CONTACT_US
          : SHARED_EVENTS.PAGE.CONTACT_US
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_CONTACT_US
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_CONTACT_US
          : SHARED_EVENTS.LINK_CLICK.FOOTER_CONTACT_US
        break
      case 'faq':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.FAQ
            : PATIENT_EVENTS.PAGE.FAQ
          : SHARED_EVENTS.PAGE.FAQ
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_FAQ
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_FAQ
          : SHARED_EVENTS.LINK_CLICK.FOOTER_FAQ
        break
      case 'privacy policy':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.PRIVACY_POLICY
            : PATIENT_EVENTS.PAGE.PRIVACY_POLICY
          : SHARED_EVENTS.PAGE.PRIVACY_POLICY
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_PRIVACY_POLICY
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_PRIVACY_POLICY
          : SHARED_EVENTS.LINK_CLICK.FOOTER_PRIVACY_POLICY
        break
      case 'terms of use':
        pageTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.PAGE.TERMS_OF_USE
            : PATIENT_EVENTS.PAGE.TERMS_OF_USE
          : SHARED_EVENTS.PAGE.TERMS_OF_USE
        linkTag = login
          ? isClinician
            ? CLINICIAN_EVENTS.LINK_CLICK.FOOTER_TERMS_OF_USE
            : PATIENT_EVENTS.LINK_CLICK.FOOTER_TERMS_OF_USE
          : SHARED_EVENTS.LINK_CLICK.FOOTER_TERMS_OF_USE
        break
      default:
        linkTag = `om_link_click:footer_${linkName.trim().replace(/\s+/g, '_').toLowerCase()}`
    }

    // Track link click event
    if (linkTag) {
      trackLink(
        {
          name: linkTag,
          location: login
            ? isClinician
              ? 'authenticated-clinician-footers'
              : 'authenticated-patient-footers'
            : 'unauthenticated-footers',
          type: linkType,
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          userType: role,
        }
      )
    }

    // Track page view event
    if (pageTag) {
      trackPage(
        {
          name: pageTag,
          is404: false,
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          userType: role,
        }
      )
    }
  }

  return (
    <Box
      component={'footer'}
      py={2}
      sx={({ ues, shadows }) => ({
        borderTop: `1px solid ${ues.neutral['20']}`,
        background: ues.neutral['0'],
        boxShadow: shadows[4],
        px: {
          xs: 2,
          sm: 2,
          md: 10.5,
          lg: 10.5,
        },
        pb: {
          xs: 10,
          sm: 2,
          md: 2,
          lg: 2,
        },
        mb: {
          xs:
            location.pathname.includes('/provider-profile') ||
            location.pathname.includes('/profile')
              ? 10.5
              : 0,
          md:
            location.pathname.includes('/provider-profile') ||
            location.pathname.includes('/profile')
              ? 15
              : 0,
          lg: 0,
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid container spacing={{ md: 2, lg: 0 }}>
          <Grid item xs={12} md={12} lg={12}>
            <Box
              sx={{
                flexDirection: { xs: 'column', sm: 'column', md: ' row' },
                gap: { xs: 1, sm: 1, md: 2, lg: 3 },
                display: 'flex',
              }}
            >
              <Typography
                noWrap
                variant={'body2'}
                mb={{ xs: 2, sm: 2, md: 0, lg: 0, xl: 0 }}
              >
                {`© ${new Date().getFullYear()} ${footNote.label}`}
                {enableOptumMatchVersion && (
                  <Typography
                    component="span"
                    variant={'body2'}
                    sx={({ ues }) => ({
                      ml: 2,
                      color: ues.neutral[60],
                    })}
                  >
                    {`UI Version ${appVersion}`}
                  </Typography>
                )}
              </Typography>
              <Box
                sx={{
                  flexDirection: { xs: 'column', sm: 'column', md: ' row' },
                  gap: { xs: 1, sm: 1, md: 2, lg: 3 },
                  display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
                }}
              >
                {footNote.primaryLinks.map((link) => (
                  <FooterLink
                    key={link.name}
                    link={link}
                    onClick={() =>
                      handleLinkClick(link.name, 'internal', link.href)
                    }
                  />
                ))}
                {footNote.secondaryLinks.map((link) => (
                  <FooterLink
                    key={link.name}
                    link={link}
                    onClick={() => {
                      handleLinkClick(link.name, 'external', link.href)
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            lg={8}
            sx={{ display: { md: 'block', lg: 'none', xl: 'none' } }}
          >
            <Box
              sx={{
                flexDirection: { xs: 'column', sm: 'column', md: ' row' },
                gap: { xs: 3, sm: 3, md: 3, lg: 3 },
                display: 'flex',
                textAlign: 'left',
                justifyContent: 'flex-start',
              }}
            >
              {footNote.primaryLinks.map((link) => (
                <FooterLink
                  key={link.name}
                  link={link}
                  onClick={() =>
                    handleLinkClick(link.name, 'internal', link.href)
                  }
                />
              ))}
              {footNote.secondaryLinks.map((link) => (
                <FooterLink
                  key={link.name}
                  link={link}
                  onClick={() =>
                    handleLinkClick(link.name, 'external', link.href)
                  }
                />
              ))}
            </Box>
          </Grid>
        </Grid>
        <IconButton
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          sx={({ ues }) => ({
            background: ues.neutral['90'],
            color: ues.neutral['0'],
          })}
          title={'scroll to top'}
          size={'small'}
        >
          <ExpandLess />
        </IconButton>
      </Box>
    </Box>
  )
}

export default Footer
